import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Achievements() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchMilestones();
  }, [user]);

  const fetchMilestones = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/scores/milestones/user/${user.id}`);
      const data = await res.json();
      setMilestones(data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All', icon: '🎯' },
    { id: 'general', label: 'General', icon: '⭐' },
    { id: 'wins', label: 'Wins', icon: '🏆' },
    { id: 'streaks', label: 'Streaks', icon: '🔥' },
    { id: 'special', label: 'Special', icon: '✨' },
  ];

  const filteredMilestones = selectedCategory === 'all'
    ? milestones
    : milestones.filter(m => m.category === selectedCategory);

  const earnedCount = milestones.filter(m => m.earned).length;
  const totalCount = milestones.length;
  const completionRate = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  // Group by game for game-specific achievements
  const gameGroups = {};
  filteredMilestones.forEach(m => {
    if (m.game_id) {
      const key = m.game_name || 'Other';
      if (!gameGroups[key]) {
        gameGroups[key] = { emoji: m.game_emoji, milestones: [] };
      }
      gameGroups[key].milestones.push(m);
    }
  });

  const generalMilestones = filteredMilestones.filter(m => !m.game_id);

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const heroStats = [
    { label: 'Earned', value: earnedCount, icon: '🏆' },
    { label: 'Remaining', value: totalCount - earnedCount, icon: '🔒' },
    { label: 'Completion', value: `${completionRate}%`, icon: '📊' },
    { label: 'Total', value: totalCount, icon: '🎯' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading achievements...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
          >
            ← Back
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-2xl font-bold text-white">🏆 Achievements</h1>
            <p className="text-slate-400 text-sm">Track your progress</p>
          </div>
          <div className="w-10" />
        </div>

        {/* Hero Stats Card */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Your Progress</h2>
            <div className="text-purple-300 text-sm">
              {earnedCount} / {totalCount} achievements
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${completionRate}%` }}
            />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            {heroStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-xl mb-1">{stat.icon}</div>
                <div className="text-xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-slate-400 uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                selectedCategory === cat.id
                  ? 'bg-white text-slate-900'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* General Achievements */}
        {generalMilestones.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
              General Achievements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {generalMilestones.map((milestone, i) => (
                <div
                  key={i}
                  className={`p-4 rounded-xl border transition-all ${
                    milestone.earned
                      ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30'
                      : 'bg-slate-800/50 border-slate-700 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`text-3xl ${!milestone.earned && 'grayscale'}`}>
                      {milestone.emoji}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold">{milestone.name}</h3>
                        {milestone.earned && (
                          <span className="text-green-400 text-sm">✓</span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{milestone.description}</p>
                      {milestone.earned ? (
                        <p className="text-amber-400 text-xs mt-1">
                          Earned {formatDate(milestone.earned_at)}
                        </p>
                      ) : (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>{milestone.current || 0} / {milestone.threshold}</span>
                            <span>{milestone.progress}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-slate-500 transition-all"
                              style={{ width: `${milestone.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Game-Specific Achievements */}
        {Object.entries(gameGroups).map(([gameName, group]) => (
          <div key={gameName} className="mb-6">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span>{group.emoji}</span>
              {gameName} Achievements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {group.milestones.map((milestone, i) => (
                <div
                  key={i}
                  className={`p-4 rounded-xl border transition-all ${
                    milestone.earned
                      ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30'
                      : 'bg-slate-800/50 border-slate-700 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`text-3xl ${!milestone.earned && 'grayscale'}`}>
                      {milestone.emoji}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold">{milestone.name}</h3>
                        {milestone.earned && (
                          <span className="text-green-400 text-sm">✓</span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{milestone.description}</p>
                      {milestone.earned ? (
                        <p className="text-amber-400 text-xs mt-1">
                          Earned {formatDate(milestone.earned_at)}
                        </p>
                      ) : (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>{milestone.current || 0} / {milestone.threshold}</span>
                            <span>{milestone.progress}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-slate-500 transition-all"
                              style={{ width: `${milestone.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredMilestones.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No achievements in this category.
          </div>
        )}
      </div>
    </div>
  );
}
